import './App.css';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8200/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '').replace(/\/+$/, '');

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'categories', label: 'Categories' },
  { id: 'products', label: 'Products' },
  { id: 'customers', label: 'Customers' },
  { id: 'orders', label: 'Orders' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'staff', label: 'Staff' },
];

const EMPTY_FORMS = {
   categories: { name: '', short_description: '', category_type: 'product', display_order: 0, is_active: true },
    products: {
      name: '',
      category: '',
      product_type: 'bracelet',
      sku: '',
      price: '',
      short_description: '',
      full_description: '',
      stock_quantity: '',
      image_file: null,
      image_preview: '',
      is_featured: false,
      is_best_seller: false,
      is_new_arrival: false,
      crystal_material: '',
      intention_tags: '',
    },
  customers: {
    full_name: '',
    email: '',
    phone_number: '',
    signup_source: 'admin',
    marketing_opt_in: false,
    notes: '',
    is_active: true,
  },
  orders: {
    customer: '',
    guest_full_name: '',
    guest_email: '',
    order_status: 'new',
    payment_status: 'pending',
    fulfillment_status: 'new',
    shipping_amount: '0.00',
    tax_amount: '0.00',
    order_notes: '',
  },
  staff: { username: '', email: '', first_name: '', last_name: '', password: '', is_active: true },
};

const RESOURCE_ENDPOINTS = {
  categories: 'categories/',
  products: 'products/',
  customers: 'customers/',
  orders: 'orders/',
  staff: 'staff/',
};

function formatApiError(data) {
  if (!data) {
    return 'Request failed. Please retry.';
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  return Object.entries(data)
    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
    .join(' | ') || 'Request failed. Please retry.';
}

function buildProductPayload(form) {
  const payload = {
    name: form.name || '',
    category: form.category ? Number(form.category) : '',
    product_type: form.product_type || 'bracelet',
    sku: form.sku || '',
    price: form.price || '',
    short_description: form.short_description || '',
    full_description: form.full_description || form.short_description || '',
    stock_quantity: form.stock_quantity || '0',
    availability_status: Number(form.stock_quantity || 0) > 0 ? 'in_stock' : 'out_of_stock',
    is_featured: form.is_featured === true || form.is_featured === 'true',
    is_best_seller: form.is_best_seller === true || form.is_best_seller === 'true',
    is_new_arrival: form.is_new_arrival === true || form.is_new_arrival === 'true',
    crystal_material: form.crystal_material || '',
    intention_tags: typeof form.intention_tags === 'string'
      ? form.intention_tags.split(',').map(tag => tag.trim()).filter(Boolean)
      : Array.isArray(form.intention_tags)
        ? form.intention_tags
        : [],
  };

  if (form.image_file) {
    payload.image_file = form.image_file;
  }

  return payload;
}

function buildOrderPayload(form) {
  return {
    customer: form.customer || null,
    guest_full_name: form.guest_full_name || '',
    guest_email: form.guest_email || '',
    guest_phone: form.guest_phone || '',
    order_status: form.order_status || 'new',
    payment_status: form.payment_status || 'pending',
    fulfillment_status: form.fulfillment_status || 'new',
    shipping_amount: form.shipping_amount || '0.00',
    tax_amount: form.tax_amount || '0.00',
    shipping_address: form.shipping_address || '',
    billing_address: form.billing_address || '',
    order_notes: form.order_notes || '',
  };
}

function resolveImageUrl(value) {
  const imagePath = value?.trim();
  if (!imagePath) {
    return '';
  }
  if (/^(https?:|blob:|data:)/i.test(imagePath)) {
    return imagePath;
  }
  return `${API_ORIGIN}/${imagePath.replace(/^\/+/, '')}`;
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const [staffUser, setStaffUser] = useState(JSON.parse(localStorage.getItem('adminStaff') || 'null'));
  const [activeView, setActiveView] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [collections, setCollections] = useState({ categories: [], products: [], customers: [], orders: [], staff: [] });
  const [editState, setEditState] = useState({ view: '', id: null });
  const [forms, setForms] = useState(EMPTY_FORMS);
  const [credentials, setCredentials] = useState({ username: 'staffadmin', password: 'staff1234' });

  const api = useCallback(async ({ method, path, data, params }) => {
    try {
      const response = await axios({
        method,
        url: `${API_BASE_URL}/${path}`,
        data,
        params,
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      return response.data;
    } catch (requestError) {
      const message = formatApiError(requestError.response?.data);
      throw new Error(message);
    }
  }, [token]);

  const listFromResponse = (data) => (Array.isArray(data) ? data : data?.results || []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [categories, products, customers, orders, staff] = await Promise.all([
        api({ method: 'get', path: 'categories/' }),
        api({ method: 'get', path: 'products/' }),
        api({ method: 'get', path: 'customers/' }),
        api({ method: 'get', path: 'orders/' }),
        api({ method: 'get', path: 'staff/' }),
      ]);
      setCollections({
        categories: listFromResponse(categories),
        products: listFromResponse(products),
        customers: listFromResponse(customers),
        orders: listFromResponse(orders),
        staff: listFromResponse(staff),
      });
    } catch (fetchError) {
      setError(fetchError.message);
      if (fetchError.message.toLowerCase().includes('credentials')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminStaff');
        setToken('');
        setStaffUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!token) return;
    fetchAllData();
  }, [token, fetchAllData]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api({ method: 'post', path: 'staff/login/', data: credentials });
      localStorage.setItem('adminToken', response.token);
      localStorage.setItem('adminStaff', JSON.stringify(response.staff));
      setToken(response.token);
      setStaffUser(response.staff);
      setNotice('Signed in successfully.');
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (callApi = true) => {
    if (callApi && token) {
      try {
        await api({ method: 'post', path: 'staff/logout/' });
      } catch (logoutError) {
        setError(logoutError.message);
      }
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminStaff');
    setToken('');
    setStaffUser(null);
    setCollections({ categories: [], products: [], customers: [], orders: [], staff: [] });
  };

  const startCreate = (resource) => {
    setEditState({ view: resource, id: null });
    setForms((previous) => ({ ...previous, [resource]: { ...EMPTY_FORMS[resource] } }));
  };

  const startEdit = (resource, row) => {
    const value = { ...EMPTY_FORMS[resource], ...row };
    if (resource === 'staff') {
      value.password = '';
    }
    if (resource === 'orders') {
      value.customer = row.customer || '';
    }
    if (resource === 'products') {
      delete value.images;
      value.image_file = null;
      value.image_preview = resolveImageUrl(row.images?.[0]?.image);
      value.intention_tags = Array.isArray(row.intention_tags) ? row.intention_tags.join(', ') : row.intention_tags || '';
    }
    setEditState({ view: resource, id: row.id });
    setForms((previous) => ({ ...previous, [resource]: value }));
  };

  const onFieldChange = (resource, field, value) => {
    setForms((previous) => ({ ...previous, [resource]: { ...previous[resource], [field]: value } }));
  };

  const saveResource = async (resource) => {
    setLoading(true);
    setError('');
    try {
      const isEditing = editState.view === resource && editState.id !== null;
      const endpoint = RESOURCE_ENDPOINTS[resource] + (isEditing ? `${editState.id}/` : '');
      const payload = resource === 'products'
        ? buildProductPayload(forms[resource])
        : resource === 'orders'
          ? buildOrderPayload(forms[resource])
          : { ...forms[resource] };
      if (resource === 'staff' && !payload.password) {
        delete payload.password;
      }
      if (resource === 'products') {
        if (payload.image_file) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              if (key === 'intention_tags' && Array.isArray(value)) {
                value.forEach(tag => formData.append('intention_tags', tag));
              } else {
                formData.append(key, typeof value === 'boolean' ? String(value) : value);
              }
            }
          });
          await api({ method: isEditing ? 'patch' : 'post', path: endpoint, data: formData });
        } else {
          delete payload.image_file;
          await api({ method: isEditing ? 'patch' : 'post', path: endpoint, data: payload });
        }
      } else {
        await api({ method: isEditing ? 'patch' : 'post', path: endpoint, data: payload });
      }
      await fetchAllData();
      setNotice(isEditing ? 'Updated successfully.' : 'Created successfully.');
      setEditState({ view: '', id: null });
      setForms((previous) => ({ ...previous, [resource]: { ...EMPTY_FORMS[resource] } }));
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setLoading(false);
    }
  };

  const archiveToggle = async (resource, row) => {
    setLoading(true);
    setError('');
    try {
      if (resource === 'orders') {
        const nextStatus = row.order_status === 'archived' ? 'new' : 'archived';
        await api({ method: 'patch', path: `${RESOURCE_ENDPOINTS.orders}${row.id}/`, data: { order_status: nextStatus } });
      } else {
        await api({
          method: 'patch',
          path: `${RESOURCE_ENDPOINTS[resource]}${row.id}/`,
          data: { is_active: !row.is_active },
        });
      }
      await fetchAllData();
    } catch (toggleError) {
      setError(toggleError.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteResource = async (resource, row) => {
    if (!window.confirm(`Delete ${row.name || row.order_number || 'this record'} permanently?`)) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await api({ method: 'delete', path: `${RESOURCE_ENDPOINTS[resource]}${row.id}/` });
      await fetchAllData();
      setNotice('Deleted successfully.');
      if (editState.view === resource && editState.id === row.id) {
        setEditState({ view: '', id: null });
        setForms((previous) => ({ ...previous, [resource]: { ...EMPTY_FORMS[resource] } }));
      }
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setLoading(false);
    }
  };

  const markOrderShipped = async (row) => {
    setLoading(true);
    setError('');
    try {
      await api({
        method: 'patch',
        path: `${RESOURCE_ENDPOINTS.orders}${row.id}/`,
        data: { fulfillment_status: 'shipped', order_status: row.order_status === 'new' ? 'processing' : row.order_status },
      });
      await fetchAllData();
      setNotice(`${row.order_number} marked as shipped.`);
    } catch (shipError) {
      setError(shipError.message);
    } finally {
      setLoading(false);
    }
  };

  const dashboardStats = useMemo(() => {
    const lowStock = collections.products.filter(
      (product) => Number(product.stock_quantity) <= Number(product.low_stock_threshold || 0)
    ).length;
    const pendingOrders = collections.orders.filter((order) => order.order_status === 'new').length;
    return [
      { label: 'Total Categories', value: collections.categories.length },
      { label: 'Active Products', value: collections.products.filter((item) => item.is_active).length },
      { label: 'Low Stock Products', value: lowStock },
      { label: 'Total Customers', value: collections.customers.length },
      { label: 'Total Orders', value: collections.orders.length },
      { label: 'Pending Orders', value: pendingOrders },
    ];
  }, [collections]);

  if (!token) {
    return (
      <div className="auth-shell">
        <form className="auth-card" onSubmit={handleLogin}>
          <p className="eyebrow">Crystal Admin</p>
          <h1>Staff Sign In</h1>
          <p className="muted">Use the default staff account or your own staff credentials.</p>
          <label>
            Username
            <input
              value={credentials.username}
              onChange={(event) => setCredentials((previous) => ({ ...previous, username: event.target.value }))}
              required
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={credentials.password}
              onChange={(event) => setCredentials((previous) => ({ ...previous, password: event.target.value }))}
              required
            />
          </label>
          {error ? <p className="error-text">{error}</p> : null}
          <button type="submit" disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div>
          <p className="eyebrow">Ma Kuku</p>
          <h2>Operations Panel</h2>
        </div>
        <nav>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              className={activeView === item.id ? 'nav-button active' : 'nav-button'}
              onClick={() => setActiveView(item.id)}>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <p>{staffUser?.username}</p>
          <button className="ghost" onClick={() => handleLogout(true)}>Logout</button>
        </div>
      </aside>

      <main className="main-panel">
        <header className="main-header">
          <h1>{NAV_ITEMS.find((item) => item.id === activeView)?.label || 'Dashboard'}</h1>
          <button className="ghost" onClick={fetchAllData} disabled={loading}>Refresh</button>
        </header>
        {notice ? <p className="notice-text">{notice}</p> : null}
        {error ? <p className="error-text">{error}</p> : null}

        {activeView === 'dashboard' ? (
          <section>
            <div className="stats-grid">
              {dashboardStats.map((item) => (
                <article key={item.label} className="stat-card">
                  <p>{item.label}</p>
                  <h3>{item.value}</h3>
                </article>
              ))}
            </div>
            <div className="split-grid">
              <ResourcePreview title="Recently Added Products" rows={collections.products.slice(0, 5)} field="name" />
              <ResourcePreview title="Recently Added Customers" rows={collections.customers.slice(0, 5)} field="full_name" />
            </div>
          </section>
        ) : null}

        {activeView === 'shipped' ? (
          <ShippedOrdersSection
            rows={collections.orders.filter((order) => order.fulfillment_status === 'shipped')}
            customers={collections.customers}
            loading={loading}
          />
        ) : null}

        {activeView !== 'dashboard' && activeView !== 'shipped' ? (
          <CrudSection
            view={activeView}
            data={collections}
            loading={loading}
            editState={editState}
            form={forms[activeView]}
            onCreate={startCreate}
            onEdit={startEdit}
            onFieldChange={onFieldChange}
            onSave={saveResource}
            onToggleArchive={archiveToggle}
            onDelete={deleteResource}
            onMarkShipped={markOrderShipped}
          />
        ) : null}
      </main>
    </div>
  );
}

function ResourcePreview({ title, rows, field }) {
  return (
    <article className="panel-card">
      <h3>{title}</h3>
      {rows.length === 0 ? <p className="muted">No records yet.</p> : null}
      <ul>
        {rows.map((row) => (
          <li key={row.id}>{row[field]}</li>
        ))}
      </ul>
    </article>
  );
}

function CrudSection({ view, data, loading, editState, form, onCreate, onEdit, onFieldChange, onSave, onToggleArchive, onDelete, onMarkShipped }) {
  const rows = data[view] || [];
  const categoriesById = Object.fromEntries((data.categories || []).map((item) => [item.id, item.name]));
  const customersById = Object.fromEntries((data.customers || []).map((item) => [item.id, item.full_name]));

  return (
    <section className="crud-layout">
      <article className="panel-card table-card">
        <div className="table-header">
          <h3>{view[0].toUpperCase() + view.slice(1)}</h3>
          <button onClick={() => onCreate(view)}>Add New</button>
        </div>
        <table>
<thead>
             <tr>
               {view === 'products' && <th>Photo</th>}
               {(view === 'categories' || view === 'products' || view === 'customers' || view === 'staff') && <th>Name</th>}
               {view === 'products' && <th>Category</th>}
               {view === 'products' && <th>Description</th>}
               {view === 'products' && <th>Stock</th>}
               {view === 'customers' && <th>Email</th>}
               {view === 'orders' && <th>Order</th>}
               {view === 'orders' && <th>Customer</th>}
               {view === 'orders' && <th>Total</th>}
               {view === 'orders' && <th>Status</th>}
               {view === 'orders' && <th>Payment</th>}
               {view === 'orders' && <th>Fulfillment</th>}
               <th>Action</th>
             </tr>
           </thead>
           <tbody>
             {rows.map((row) => (
               <tr key={row.id}>
{view === 'products' && (
                    <td>
                      {resolveImageUrl(row.images?.[0]?.image) ? (
                        <img src={resolveImageUrl(row.images?.[0]?.image)} alt={row.name} className="product-thumb" />
                      ) : (
                        <span className="muted">-</span>
                      )}
                    </td>
                  )}
                 {(view === 'categories' || view === 'products') && <td>{row.name}</td>}
                 {view === 'customers' && <td>{row.full_name}</td>}
                 {view === 'staff' && <td>{row.username}</td>}
                 {view === 'products' && <td>{categoriesById[row.category] || 'N/A'}</td>}
                 {view === 'products' && <td className="description-cell">{row.short_description || row.full_description || '-'}</td>}
                 {view === 'products' && <td>{row.stock_quantity}</td>}
                 {view === 'customers' && <td>{row.email}</td>}
                 {view === 'orders' && <td>{row.order_number}</td>}
                 {view === 'orders' && <td>{customersById[row.customer] || row.guest_full_name || 'Guest'}</td>}
                 {view === 'orders' && <td>INR {Number(row.total_amount || 0).toLocaleString('en-IN')}</td>}
                 {view === 'orders' && <td><span className="badge">{row.order_status}</span></td>}
                 {view === 'orders' && <td><span className="badge">{row.payment_status}</span></td>}
                 {view === 'orders' && <td><span className="badge">{row.fulfillment_status}</span></td>}
                <td>
                  <div className="row-actions">
                    <button className="ghost" onClick={() => onEdit(view, row)}>Edit</button>
                    {view === 'products' ? (
                      <button className="danger" onClick={() => onDelete(view, row)}>Delete</button>
                    ) : (
                      <>
                        {view === 'orders' && row.fulfillment_status !== 'shipped' ? (
                          <button className="ship-button" onClick={() => onMarkShipped(row)}>Ship</button>
                        ) : null}
                        <button className="ghost" onClick={() => onToggleArchive(view, row)}>
                          {view === 'orders'
                            ? row.order_status === 'archived' ? 'Restore' : 'Archive'
                            : row.is_active ? 'Archive' : 'Restore'}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
{rows.length === 0 && (
               <tr>
                 <td colSpan={view === 'orders' ? 7 : view === 'products' ? 6 : 4} className="muted">{loading ? 'Loading records...' : 'No records available.'}</td>
               </tr>
             )}
          </tbody>
        </table>
      </article>

      <article className="panel-card form-card">
        <h3>{editState.view === view && editState.id ? 'Edit Record' : 'New Record'}</h3>
        <DynamicForm view={view} data={data} form={form} onFieldChange={onFieldChange} />
        <button onClick={() => onSave(view)} disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
      </article>
    </section>
  );
}

function ShippedOrdersSection({ rows, customers, loading }) {
  const customersById = Object.fromEntries((customers || []).map((item) => [item.id, item.full_name]));

  return (
    <section className="panel-card table-card">
      <div className="table-header">
        <div>
          <h3>Shipped Bracelets</h3>
          <p className="muted">Orders marked shipped from the orders table appear here for quick tracking.</p>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Order</th>
            <th>Customer</th>
            <th>Total</th>
            <th>Payment</th>
            <th>Fulfillment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.order_number}</td>
              <td>{customersById[row.customer] || row.guest_full_name || 'Guest'}</td>
              <td>INR {Number(row.total_amount || 0).toLocaleString('en-IN')}</td>
              <td><span className="badge">{row.payment_status}</span></td>
              <td><span className="badge shipped-badge">{row.fulfillment_status}</span></td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan="5" className="muted">{loading ? 'Loading shipped orders...' : 'No shipped bracelet orders yet.'}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}

function DynamicForm({ view, data, form, onFieldChange }) {
  const categories = data.categories || [];
  const customers = data.customers || [];

  if (view === 'categories') {
    return (
      <div className="form-grid">
        <label>Name<input value={form.name || ''} onChange={(event) => onFieldChange(view, 'name', event.target.value)} required /></label>
        <label>Type
          <select value={form.category_type || 'product'} onChange={(event) => onFieldChange(view, 'category_type', event.target.value)}>
            <option value="product">Product</option>
            <option value="intention">Intention</option>
          </select>
        </label>
        <label>Display Order<input type="number" value={form.display_order ?? 0} onChange={(event) => onFieldChange(view, 'display_order', Number(event.target.value))} /></label>
        <label className="full-width">Short Description<textarea value={form.short_description || ''} onChange={(event) => onFieldChange(view, 'short_description', event.target.value)} /></label>
      </div>
    );
  }

  if (view === 'products') {
    return (
      <div className="form-grid">
        <label>Name<input value={form.name || ''} onChange={(event) => onFieldChange(view, 'name', event.target.value)} required /></label>
        <label>Category
          <select value={form.category || ''} onChange={(event) => onFieldChange(view, 'category', Number(event.target.value))}>
            <option value="">Select category</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
        </label>
        <label>Product Type
          <select value={form.product_type || 'bracelet'} onChange={(event) => onFieldChange(view, 'product_type', event.target.value)}>
            <option value="bracelet">Bracelet</option>
            <option value="stone">Stone</option>
            <option value="bundle">Bundle</option>
          </select>
        </label>
        <label>SKU<input value={form.sku || ''} onChange={(event) => onFieldChange(view, 'sku', event.target.value)} required /></label>
        <label>Price<input type="number" step="0.01" value={form.price || ''} onChange={(event) => onFieldChange(view, 'price', event.target.value)} required /></label>
        <label>Stock Quantity<input type="number" value={form.stock_quantity || ''} onChange={(event) => onFieldChange(view, 'stock_quantity', event.target.value)} /></label>
        <label>Crystal Material<input value={form.crystal_material || ''} onChange={(event) => onFieldChange(view, 'crystal_material', event.target.value)} placeholder="e.g. Rose Quartz, Amethyst" /></label>
        <label>Intention Tags<input value={form.intention_tags || ''} onChange={(event) => onFieldChange(view, 'intention_tags', event.target.value)} placeholder="e.g. Calm, Love, Protection, Focus" /></label>
        
        <div className="full-width checkboxes-row" style={{ display: 'flex', gap: '1.5rem', margin: '0.5rem 0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_featured || false} onChange={(event) => onFieldChange(view, 'is_featured', event.target.checked)} />
            Featured Pick
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_best_seller || false} onChange={(event) => onFieldChange(view, 'is_best_seller', event.target.checked)} />
            Best Seller
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_new_arrival || false} onChange={(event) => onFieldChange(view, 'is_new_arrival', event.target.checked)} />
            New Arrival
          </label>
        </div>

        <label className="full-width">Short Description<textarea value={form.short_description || ''} onChange={(event) => onFieldChange(view, 'short_description', event.target.value)} placeholder="Brief text shown after opening the product"></textarea></label>
        <label className="full-width">Full Description<textarea value={form.full_description || ''} onChange={(event) => onFieldChange(view, 'full_description', event.target.value)} placeholder="Detailed product description"></textarea></label>
        <div className="full-width">
          <label>Product Image
            <input type="file" accept="image/*" onChange={(event) => {
              const file = event.target.files?.[0] || null;
              onFieldChange(view, 'image_file', file);
              onFieldChange(view, 'image_preview', file ? URL.createObjectURL(file) : form.image_preview || '');
            }} />
          </label>
          {form.image_preview ? (
            <img src={form.image_preview} alt="Preview" style={{ maxHeight: '72px', maxWidth: '72px', objectFit: 'contain', marginTop: '0.6rem' }} />
          ) : (
            <p className="muted">No image added.</p>
          )}
        </div>
      </div>
    );
  }

  if (view === 'customers') {
    return (
      <div className="form-grid">
        <label>Full Name<input value={form.full_name || ''} onChange={(event) => onFieldChange(view, 'full_name', event.target.value)} required /></label>
        <label>Email<input type="email" value={form.email || ''} onChange={(event) => onFieldChange(view, 'email', event.target.value)} required /></label>
        <label>Phone<input value={form.phone_number || ''} onChange={(event) => onFieldChange(view, 'phone_number', event.target.value)} /></label>
        <label>Signup Source<input value={form.signup_source || ''} onChange={(event) => onFieldChange(view, 'signup_source', event.target.value)} /></label>
        <label className="full-width">Notes<textarea value={form.notes || ''} onChange={(event) => onFieldChange(view, 'notes', event.target.value)} /></label>
      </div>
    );
  }

  if (view === 'orders') {
    return (
      <div className="form-grid">
        <label>Customer
          <select value={form.customer || ''} onChange={(event) => onFieldChange(view, 'customer', event.target.value ? Number(event.target.value) : '')}>
            <option value="">Guest order</option>
            {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.full_name}</option>)}
          </select>
        </label>
        <label>Guest Name<input value={form.guest_full_name || ''} onChange={(event) => onFieldChange(view, 'guest_full_name', event.target.value)} /></label>
        <label>Guest Email<input type="email" value={form.guest_email || ''} onChange={(event) => onFieldChange(view, 'guest_email', event.target.value)} /></label>
        <label>Guest Phone<input value={form.guest_phone || ''} onChange={(event) => onFieldChange(view, 'guest_phone', event.target.value)} /></label>
        <label>Status
          <select value={form.order_status || 'new'} onChange={(event) => onFieldChange(view, 'order_status', event.target.value)}>
            <option value="new">New</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="archived">Archived</option>
          </select>
        </label>
        <label>Payment
          <select value={form.payment_status || 'pending'} onChange={(event) => onFieldChange(view, 'payment_status', event.target.value)}>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </label>
        <label>Fulfillment
          <select value={form.fulfillment_status || 'new'} onChange={(event) => onFieldChange(view, 'fulfillment_status', event.target.value)}>
            <option value="new">New</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
        <label>Shipping<input type="number" step="0.01" value={form.shipping_amount || '0.00'} onChange={(event) => onFieldChange(view, 'shipping_amount', event.target.value)} /></label>
        <label>Tax<input type="number" step="0.01" value={form.tax_amount || '0.00'} onChange={(event) => onFieldChange(view, 'tax_amount', event.target.value)} /></label>
        <label className="full-width">Shipping Address<textarea value={form.shipping_address || ''} onChange={(event) => onFieldChange(view, 'shipping_address', event.target.value)} /></label>
        <label className="full-width">Order Notes<textarea value={form.order_notes || ''} onChange={(event) => onFieldChange(view, 'order_notes', event.target.value)} /></label>
      </div>
    );
  }

  return (
    <div className="form-grid">
      <label>Username<input value={form.username || ''} onChange={(event) => onFieldChange(view, 'username', event.target.value)} required /></label>
      <label>Email<input type="email" value={form.email || ''} onChange={(event) => onFieldChange(view, 'email', event.target.value)} /></label>
      <label>First Name<input value={form.first_name || ''} onChange={(event) => onFieldChange(view, 'first_name', event.target.value)} /></label>
      <label>Last Name<input value={form.last_name || ''} onChange={(event) => onFieldChange(view, 'last_name', event.target.value)} /></label>
      <label className="full-width">Password<input type="password" value={form.password || ''} onChange={(event) => onFieldChange(view, 'password', event.target.value)} placeholder="Set for new staff, optional while editing" /></label>
    </div>
  );
}

export default App;
